/* =====================================================
   Date pentru adresa de facturare: țări (nume în română din Intl)
   și județele României + București, cu căutare fără diacritice.
===================================================== */

export const DEFAULT_COUNTRY = 'RO';

// ISO 3166-1 alpha-2, fără teritoriile nelocuite.
const COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT ' +
  'BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ' +
  'ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ' +
  'ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS ' +
  'LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG ' +
  'NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD ' +
  'SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ ' +
  'UA UG US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

let countryOptionsCache = null;

/** [{ code, name }] — România prima, restul în ordine alfabetică românească. */
export const countryOptions = () => {
  if (countryOptionsCache) return countryOptionsCache;
  let displayNames = null;
  try {
    displayNames = new Intl.DisplayNames(['ro'], { type: 'region' });
  } catch (err) {
    // Browser fără Intl.DisplayNames: rămân codurile, România își păstrează numele.
  }
  const nameOf = (code) => (code === DEFAULT_COUNTRY ? 'România' : displayNames?.of(code) || code);
  const others = COUNTRY_CODES.filter((code) => code !== DEFAULT_COUNTRY)
    .map((code) => ({ code, name: nameOf(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ro'));
  countryOptionsCache = [{ code: DEFAULT_COUNTRY, name: nameOf(DEFAULT_COUNTRY) }, ...others];
  return countryOptionsCache;
};

export const COUNTIES = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brașov', 'Brăila',
  'București', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj', 'Constanța', 'Covasna', 'Dâmbovița',
  'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov',
  'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu',
  'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vaslui', 'Vâlcea', 'Vrancea',
].sort((a, b) => a.localeCompare(b, 'ro'));

// „Ș/ș" cu virgulă și „Ş/ş" cu sedilă se reduc la fel: NFD + eliminarea semnelor diacritice.
const fold = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const ALIASES = { bucharest: 'București', 'municipiul bucuresti': 'București' };
const FOLDED = COUNTIES.map((name) => ({ name, key: fold(name), words: fold(name).split(/[\s-]+/) }));

/** Numele canonic al județului pentru un text tastat sau completat automat, altfel null. */
export const resolveCounty = (text) => {
  const key = fold(text).replace(/^(judetul|jud\.?)\s+/, '');
  if (!key) return null;
  if (ALIASES[key]) return ALIASES[key];
  return FOLDED.find((county) => county.key === key)?.name || null;
};

/** Județele potrivite cu textul: întâi cele care încep cu el, apoi cele cu un cuvânt care începe cu el. */
export const matchCounties = (text) => {
  const query = fold(text);
  if (!query) return COUNTIES;
  const starts = FOLDED.filter((county) => county.key.startsWith(query));
  const words = FOLDED.filter(
    (county) => !county.key.startsWith(query) && county.words.some((word) => word.startsWith(query))
  );
  return [...starts, ...words].map((county) => county.name);
};
